import React from 'react';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { TabsContent } from '@/components/ui/tabs';
import { Sparkles, Bot, Layers, ListChecks } from 'lucide-react';
import { SECTION_BY_KEY } from '@/lib/resumeTemplateCatalog';
import {
  FieldGroup,
  AddSectionPopover,
  RemoveSectionButton,
  SectionAIHint,
} from '@/components/resumeTemplate/ResumeTemplateSectionControls';

export function ResumeTemplateEditorTabs({
  tpl,
  setTpl,
  brandOk,
  updateSection,
  removeSection,
  addSection,
}) {
  return (
    <>
      {/* ---------- BRAND & LAYOUT ---------- */}
      <TabsContent value="brand" className="space-y-5 p-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FieldGroup label="Template name" htmlFor="tpl-name">
            <Input
              id="tpl-name"
              value={tpl.name}
              onChange={(e) => setTpl({ ...tpl, name: e.target.value })}
              data-testid="resume-template-name-input"
            />
          </FieldGroup>
          <FieldGroup label="Brand colour (hex)" htmlFor="tpl-brand">
            <div className="flex items-center gap-2">
              <Input
                id="tpl-brand"
                value={tpl.brand_color || ''}
                placeholder="#0078D4"
                onChange={(e) => setTpl({ ...tpl, brand_color: e.target.value })}
                data-testid="resume-template-brand-color-input"
                aria-invalid={!brandOk}
                className={brandOk ? '' : 'border-destructive focus-visible:ring-destructive'}
              />
              <span
                className="h-9 w-9 shrink-0 rounded-md border"
                style={{ background: brandOk && tpl.brand_color ? tpl.brand_color : 'transparent' }}
                aria-hidden="true"
                data-testid="resume-template-brand-swatch"
              />
            </div>
            {!brandOk && (
              <p className="mt-1 text-xs text-destructive" data-testid="resume-template-brand-error">
                Must be a 6-digit hex like <code className="font-mono">#0078D4</code>.
              </p>
            )}
          </FieldGroup>
        </div>

        <Separator />

        <div className="flex items-center justify-between rounded-xl border bg-card p-3">
          <div className="pr-4">
            <p className="text-sm font-semibold">Show candidate photo / initials</p>
            <p className="text-xs text-muted-foreground">
              Displays a circular avatar in the resume header. When no photo is uploaded, candidate initials are used.
            </p>
          </div>
          <Switch
            checked={!!tpl.show_photo}
            onCheckedChange={(v) => setTpl({ ...tpl, show_photo: v })}
            data-testid="resume-template-show-photo-switch"
          />
        </div>

        <div className="flex items-center justify-between rounded-xl border bg-card p-3">
          <div className="pr-4">
            <p className="text-sm font-semibold">Show candidate contact details</p>
            <p className="text-xs text-muted-foreground">
              When off, the candidate&apos;s email, phone, and LinkedIn are hidden from the standardised resume so a shared CV cannot be used to identify or reach the candidate directly. Off by default.
            </p>
          </div>
          <Switch
            checked={!!tpl.show_contact_details}
            onCheckedChange={(v) => setTpl({ ...tpl, show_contact_details: v })}
            data-testid="resume-template-show-contact-details-switch"
          />
        </div>

        <div className="rounded-xl border-2 border-dashed bg-secondary/40 p-3">
          <p className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-primary">
            <Sparkles className="h-3 w-3" /> How parsing works
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            When HR uploads a resume, the AI engine extracts entities (header, summary, experience, skills) and populates this template. HR can edit the result before sharing with clients.
          </p>
        </div>
      </TabsContent>

      {/* ---------- SECTIONS ---------- */}
      <TabsContent value="sections" className="space-y-3 p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold">Sections (toggle, reorder &amp; remove)</p>
            <p className="text-xs text-muted-foreground">
              Each section is rendered only if enabled. AI fills these from the uploaded resume; HR can edit the result per candidate. Use the <strong>Order</strong> field (1–9) to control vertical position.
            </p>
          </div>
          <AddSectionPopover
            existingKeys={tpl.sections.map((s) => s.key)}
            onAdd={(entry) => addSection(entry)}
          />
        </div>

        <ul className="space-y-2" data-testid="resume-template-sections">
          {[...tpl.sections]
            .map((s, originalIdx) => ({ ...s, originalIdx }))
            .sort((a, b) => a.order - b.order)
            .map((s) => {
              const i = s.originalIdx;
              const catalog = SECTION_BY_KEY[s.key];
              return (
                <li
                  key={s.key}
                  className={`group rounded-xl border bg-card p-3 transition-shadow hover:shadow-sm ${
                    s.enabled ? 'border-l-4 border-l-primary/60' : 'border-l-4 border-l-muted opacity-80'
                  }`}
                  data-testid={`resume-template-section-row-${s.key}`}
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="secondary" className="font-mono text-[10px] uppercase">
                      {s.key}
                    </Badge>
                    <Input
                      value={s.label}
                      onChange={(e) => updateSection(i, 'label', e.target.value)}
                      className="h-8 max-w-[220px] flex-1"
                      aria-label={`Label for ${s.key} section`}
                      data-testid={`resume-template-section-label-${s.key}`}
                    />
                    <FieldGroup label="Order" inline htmlFor={`order-${s.key}`}>
                      <Input
                        id={`order-${s.key}`}
                        type="number"
                        min={1}
                        max={9}
                        value={s.order}
                        onChange={(e) => updateSection(i, 'order', Number(e.target.value || 1))}
                        className="h-8 w-16"
                        data-testid={`resume-template-section-order-${s.key}`}
                      />
                    </FieldGroup>
                    <div className="ml-auto flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">
                        {s.enabled ? 'Enabled' : 'Hidden'}
                      </span>
                      <Switch
                        checked={!!s.enabled}
                        onCheckedChange={(v) => updateSection(i, 'enabled', v)}
                        data-testid={`resume-template-section-enabled-${s.key}`}
                      />
                      <RemoveSectionButton
                        sectionKey={s.key}
                        sectionLabel={s.label}
                        onConfirm={() => removeSection(s.key)}
                      />
                    </div>
                  </div>
                  {catalog?.description && (
                    <p className="mt-1.5 pl-1 text-[11px] text-muted-foreground">{catalog.description}</p>
                  )}
                  <SectionAIHint
                    sectionKey={s.key}
                    value={s.ai_hint || ''}
                    onChange={(v) => updateSection(i, 'ai_hint', v)}
                  />
                </li>
              );
            })}
        </ul>

        {tpl.sections.length === 0 && (
          <div className="rounded-xl border-2 border-dashed bg-secondary/30 p-6 text-center" data-testid="resume-template-sections-empty">
            <Layers className="mx-auto h-6 w-6 text-muted-foreground" />
            <p className="mt-2 text-sm font-medium">No sections in this template.</p>
            <p className="text-xs text-muted-foreground">Add at least one section using the &ldquo;Add section&rdquo; button above.</p>
          </div>
        )}
      </TabsContent>

      {/* ---------- AI PARSING ---------- */}
      <TabsContent value="ai" className="space-y-5 p-5" data-testid="resume-template-ai-tab">
        <div className="rounded-xl border bg-secondary/40 p-4">
          <p className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-primary">
            <Bot className="h-3 w-3" /> How AI parsing works
          </p>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            When HR uploads a resume, HireIQ extracts content into the sections enabled on this
            template. Extraction guidance is version-pinned with the template so past resumes stay
            reproducible. Product defaults apply automatically — vendor, model, and prompt settings
            are not exposed on this screen.
          </p>
        </div>

        <div className="space-y-3 rounded-xl border bg-card p-4">
          <p className="inline-flex items-center gap-1.5 text-sm font-semibold">
            <ListChecks className="h-4 w-4 text-primary" /> Configure extraction on Sections
          </p>
          <ul className="list-disc space-y-1.5 pl-5 text-sm text-muted-foreground">
            <li>Toggle, reorder, and label the sections the AI should fill.</li>
            <li>
              Optionally open each section&apos;s <strong>AI extraction hint</strong> to guide tone
              or emphasis for that block only.
            </li>
            <li>HR can still edit every field on a candidate&apos;s standardised resume after parsing.</li>
          </ul>
          <p className="text-xs text-muted-foreground" data-testid="resume-template-ai-defaults-note">
            Parsing uses safe product defaults. Advanced engine settings are not editable here.
          </p>
        </div>
      </TabsContent>

      {/* ---------- FOOTER ---------- */}
      <TabsContent value="footer" className="space-y-4 p-5">
        <FieldGroup label="Footer note" htmlFor="tpl-footer" hint="Appears at the bottom of every standardised resume (e.g. confidentiality reminder).">
          <Textarea
            id="tpl-footer"
            rows={4}
            value={tpl.footer_note || ''}
            onChange={(e) => setTpl({ ...tpl, footer_note: e.target.value })}
            data-testid="resume-template-footer-input"
          />
        </FieldGroup>
      </TabsContent>
    </>
  );
}
